export type ThemeTokens = {
  canvas: string;
  paper: string;
  surface: string;
  primary: string;
  primaryHover: string;
  primarySoft: string;
  accent: string;
  text: string;
  textMuted: string;
  border: string;
  onPrimary: string;
  sidebar: string;
  sidebarText: string;
  heading: string;
};

export const THEME_CATALOG = [
  { id: 'pumpkin', label: 'Orange', swatch: '#FF9100', tokens: ['#FCFCF7', '#FFD4A3', '#FFFCF8', '#9A4B00', '#6C3200', '#FFE1BE', '#5A2800', '#2B1D14', '#755F4F', '#E8C7A7', '#FFFFFF', '#F1C17F', '#2B1D14', '#8A4300'] },
  { id: 'lime', label: 'Green', swatch: '#C7D66D', tokens: ['#FCFCF7', '#DDE6A8', '#FEFFF8', '#59651F', '#3F4912', '#E9EFC7', '#35400D', '#212617', '#626950', '#D0D8A8', '#FFFFFF', '#D7E18F', '#212617', '#4D5918'] },
  { id: 'violet', label: 'Purple', swatch: '#9A4D95', tokens: ['#FCFCF7', '#DDB9DA', '#FFFBFF', '#783773', '#542450', '#EED8EC', '#461C43', '#281B27', '#705C6E', '#DFC7DC', '#FFFFFF', '#D7AED3', '#281B27', '#6B2F67'] }
] as const;

export type ThemeId = typeof THEME_CATALOG[number]['id'];
export const DEFAULT_THEME_ID: ThemeId = 'pumpkin';

const themeIds = new Set<string>(THEME_CATALOG.map((theme) => theme.id));

const LEGACY_THEME_MAP: Record<string, ThemeId> = {
  'cocoa': 'violet',
  'olive': 'lime',
  'cream': 'pumpkin',
  'gold': 'pumpkin',
  'sunshine': 'pumpkin',
  'tangerine': 'pumpkin',
  'vanilla': 'lime',
  'ember': 'violet',
  'ember-crypt': 'violet',
  'moonlit-coven': 'violet',
  'graveyard-moss': 'lime',
  'blood-moon-velvet': 'violet',
  'phantom-lantern': 'pumpkin',
  'salem-smoke': 'violet',
  'cursed-caramel': 'pumpkin',
  'spectral-sage': 'lime',
  'midnight-cauldron': 'violet',
  'rusted-coffin': 'violet',
  'wraith-blossom': 'violet',
  'harvest-hex': 'pumpkin',
  'batwing-plum': 'violet',
  'haunted-porcelain': 'pumpkin',
  'infernal-orchard': 'pumpkin'
};

export const isThemeId = (value: unknown): value is ThemeId =>
  typeof value === 'string' && themeIds.has(value);

export const normalizeThemeId = (value: unknown): ThemeId | null => {
  if (isThemeId(value)) return value;
  return typeof value === 'string' ? LEGACY_THEME_MAP[value] ?? null : null;
};

export const getTheme = (themeId: ThemeId) =>
  THEME_CATALOG.find((theme) => theme.id === themeId) ?? THEME_CATALOG[0];

export const applyTheme = (themeId: ThemeId) => {
  const theme = getTheme(themeId);
  const names = ['canvas', 'paper', 'surface', 'primary', 'primary-hover', 'primary-soft', 'accent', 'text', 'text-muted', 'border', 'on-primary', 'sidebar', 'sidebar-text', 'heading'];
  names.forEach((name, index) => document.documentElement.style.setProperty(`--color-${name}`, theme.tokens[index]));
  document.documentElement.dataset.theme = themeId;
};
